import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, UserPlus, Wallet } from "lucide-react";
import { toast } from "sonner";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const selCls = "w-full bg-[#121212] border-0 border-b border-white/20 text-white font-mont text-sm py-2 focus:outline-none focus:border-[#D4AF37]";
const taCls = "w-full bg-transparent border-0 border-b border-white/20 px-0 py-2 text-white font-mont text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#D4AF37] resize-none";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MODES = ["", "Bank Transfer", "UPI", "Cash", "Cheque", "Other"];
const KIND_LABEL = { salary: "Salary", advance: "Advance", bonus: "Bonus" };

const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const ym = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
};

// salary due for a month, honouring salary changes ("from" month onwards)
export function dueFor(emp, month) {
  const hist = [...(emp.salary_history || [])].sort((a, b) => (a.from < b.from ? -1 : 1));
  if (hist.length === 0) return Number(emp.monthly_salary) || 0;
  let amt = hist[0].amount;
  hist.forEach((h) => { if (h.from <= month) amt = h.amount; });
  return Number(amt) || 0;
}
export function worksInMonth(emp, month) {
  if (emp.joined && emp.joined.slice(0, 7) > month) return false;
  if (emp.left && emp.left.slice(0, 7) < month) return false;
  return true;
}

const emptyEmp = { name: "", designation: "", phone: "", joined: "", left: "", monthly_salary: "", effective_from: "", notes: "" };

export default function SalaryTab({ profileId }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthNo, setMonthNo] = useState(now.getMonth() + 1);
  const month = ym(year, monthNo);

  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // employee dialog
  const [empOpen, setEmpOpen] = useState(false);
  const [empEditing, setEmpEditing] = useState(null);
  const [empForm, setEmpForm] = useState(emptyEmp);
  // payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payEditing, setPayEditing] = useState(null);
  const [payForm, setPayForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const [e, p] = await Promise.all([
        api.get(`/salary/employees?profile_id=${profileId}`),
        api.get(`/salary/payments?profile_id=${profileId}&month=${month}`),
      ]);
      setEmployees(e.data);
      setPayments(p.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load salary data");
      setEmployees([]); setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [profileId, month]);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => { const y = now.getFullYear(); return [y - 3, y - 2, y - 1, y, y + 1]; }, []); // eslint-disable-line

  const rows = useMemo(() => {
    const byEmp = {};
    payments.forEach((p) => { (byEmp[p.employee_id] = byEmp[p.employee_id] || []).push(p); });
    return employees
      .filter((e) => worksInMonth(e, month) || (byEmp[e.id] || []).length > 0)
      .map((e) => {
        const list = byEmp[e.id] || [];
        const due = worksInMonth(e, month) ? dueFor(e, month) : 0;
        const paid = list.filter((p) => p.kind !== "bonus").reduce((s, p) => s + p.amount, 0);
        const bonus = list.filter((p) => p.kind === "bonus").reduce((s, p) => s + p.amount, 0);
        const balance = Math.max(0, due - paid);
        let status = "pending";
        if (due > 0 && paid >= due) status = "paid";
        else if (paid > 0) status = "partial";
        else if (due === 0) status = "none";
        return { e, list, due, paid, bonus, balance, status };
      });
  }, [employees, payments, month]);

  const totals = useMemo(() => ({
    due: rows.reduce((s, r) => s + r.due, 0),
    paid: rows.reduce((s, r) => s + r.paid + r.bonus, 0),
    pending: rows.reduce((s, r) => s + r.balance, 0),
  }), [rows]);

  // ----- employees -----
  const openNewEmp = () => { setEmpEditing(null); setEmpForm(emptyEmp); setEmpOpen(true); };
  const openEditEmp = (e) => {
    setEmpEditing(e);
    setEmpForm({ name: e.name, designation: e.designation || "", phone: e.phone || "", joined: e.joined || "", left: e.left || "", monthly_salary: e.monthly_salary, effective_from: "", notes: e.notes || "" });
    setEmpOpen(true);
  };
  const salaryChanged = empEditing && Number(empForm.monthly_salary) !== Number(empEditing.monthly_salary);

  const saveEmp = async () => {
    if (saving || savingRef.current) return;
    if (!empForm.name.trim()) { toast.error("Enter the employee's name"); return; }
    if (!(Number(empForm.monthly_salary) >= 0) || empForm.monthly_salary === "") { toast.error("Enter the monthly salary"); return; }
    savingRef.current = true; setSaving(true);
    try {
      const body = { ...empForm, monthly_salary: Number(empForm.monthly_salary), effective_from: salaryChanged ? (empForm.effective_from || month) : "" };
      if (empEditing) await api.put(`/salary/employees/${empEditing.id}`, body);
      else await api.post(`/salary/employees?profile_id=${profileId}`, body);
      toast.success(empEditing ? "Employee updated" : "Employee added");
      setEmpOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save employee");
    } finally { savingRef.current = false; setSaving(false); }
  };
  const deleteEmp = async () => {
    if (!empEditing || saving) return;
    if (!window.confirm(`Delete ${empEditing.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/salary/employees/${empEditing.id}`);
      toast.success("Employee deleted");
      setEmpOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  // ----- payments -----
  const openPay = (r, kind = "salary") => {
    setPayEditing(null);
    setPayForm({
      employee_id: r.e.id, name: r.e.name, month, date: todayLocal(),
      amount: kind === "salary" && r.balance > 0 ? r.balance : "", kind, mode: "Bank Transfer", note: "",
    });
    setPayOpen(true);
  };
  const openEditPay = (p, e) => {
    setPayEditing(p);
    setPayForm({ employee_id: p.employee_id, name: e.name, month: p.month, date: p.date, amount: p.amount, kind: p.kind, mode: p.mode || "", note: p.note || "" });
    setPayOpen(true);
  };
  const savePay = async () => {
    if (saving || savingRef.current) return;
    if (!(Number(payForm.amount) > 0)) { toast.error("Enter the amount"); return; }
    if (!payForm.date) { toast.error("Choose the date paid"); return; }
    savingRef.current = true; setSaving(true);
    try {
      const { name, ...rest } = payForm; // eslint-disable-line no-unused-vars
      const body = { ...rest, amount: Number(payForm.amount) };
      if (payEditing) await api.put(`/salary/payments/${payEditing.id}`, body);
      else await api.post(`/salary/payments?profile_id=${profileId}`, body);
      toast.success("Payment saved");
      setPayOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save payment");
    } finally { savingRef.current = false; setSaving(false); }
  };
  const deletePay = async (p) => {
    if (!window.confirm("Delete this payment record?")) return;
    try { await api.delete(`/salary/payments/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  const badge = (s) => {
    const map = { paid: ["Paid", "text-emerald-400 border-emerald-400/40"], partial: ["Part paid", "text-[#D4AF37] border-[#D4AF37]/40"], pending: ["Pending", "text-red-400 border-red-400/40"], none: ["—", "text-neutral-600 border-white/10"] };
    const [t, c] = map[s];
    return <span className={`font-mont text-[9px] uppercase tracking-[0.2em] border px-2 py-0.5 ${c}`}>{t}</span>;
  };

  return (
    <div data-testid="salary-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div className="flex gap-3">
          <select data-testid="salary-month" value={monthNo} onChange={(e) => setMonthNo(Number(e.target.value))} className="bg-[#121212] border border-white/20 text-white font-mont text-sm px-3 h-10">
            {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
          </select>
          <select data-testid="salary-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-[#121212] border border-white/20 text-white font-mont text-sm px-3 h-10">
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        </div>
        <Button data-testid="add-employee-btn" onClick={openNewEmp} className="rounded-none bg-white text-black hover:bg-neutral-200">
          <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.75} /> Add Employee
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
        {[["Salary due", totals.due, "text-white", "due"], ["Paid", totals.paid, "text-emerald-400", "paid"], ["Pending", totals.pending, totals.pending > 0 ? "text-red-400" : "text-neutral-400", "pending"]].map(([l, v, c, k]) => (
          <div key={l} className="bg-[#121212] p-5">
            <div className={labelCls}>{l} · {MONTHS[monthNo - 1]} {year}</div>
            <div data-testid={`salary-total-${k}`} className={`font-serif text-2xl mt-2 ${c}`}>{fmtINR(v)}</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {loading ? (
          <div className="py-14 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            {employees.length === 0 ? "No employees yet — add your first employee" : "No employees on payroll for this month"}
          </div>
        ) : rows.map((r) => (
          <div key={r.e.id} data-testid={`salary-row-${r.e.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
              <button onClick={() => setExpanded((x) => ({ ...x, [r.e.id]: !x[r.e.id] }))} className="flex items-center gap-3 min-w-0 text-left flex-1">
                {expanded[r.e.id] ? <ChevronDown className="w-4 h-4 text-neutral-500 flex-shrink-0" strokeWidth={1.5} /> : <ChevronRight className="w-4 h-4 text-neutral-500 flex-shrink-0" strokeWidth={1.5} />}
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">{r.e.name}{r.e.left && <span className="font-mont text-[9px] text-neutral-600 ml-2">left {fmtDate(r.e.left)}</span>}</div>
                  <div className="font-mont text-[10px] text-neutral-500 truncate">{r.e.designation || "—"}</div>
                </div>
              </button>
              <div className="flex items-center gap-5 flex-shrink-0">
                <div className="text-right hidden sm:block"><div className={labelCls}>Due</div><div className="font-mont text-sm text-white">{fmtINR(r.due)}</div></div>
                <div className="text-right"><div className={labelCls}>Paid</div><div className="font-mont text-sm text-emerald-400">{fmtINR(r.paid + r.bonus)}</div></div>
                <div className="text-right hidden sm:block"><div className={labelCls}>Balance</div><div className={`font-mont text-sm ${r.balance > 0 ? "text-red-400" : "text-neutral-500"}`}>{fmtINR(r.balance)}</div></div>
                {badge(r.status)}
                <Button data-testid={`pay-${r.e.id}`} onClick={() => openPay(r)} variant="outline" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 h-8 px-3 text-xs">
                  <Wallet className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Pay
                </Button>
                <button title="Edit employee" onClick={() => openEditEmp(r.e)} className="text-neutral-600 hover:text-white"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
            {expanded[r.e.id] && (
              <div className="bg-white/[0.02] px-4 pb-4 pl-11">
                {r.list.length === 0 ? (
                  <div className="font-mont text-[11px] text-neutral-600 py-3">No payments recorded for {MONTHS[monthNo - 1]} {year}.</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {r.list.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <div className="font-mont text-xs text-neutral-300">{KIND_LABEL[p.kind]} · {fmtDate(p.date)}{p.mode && ` · ${p.mode}`}{p.created_by_name && <span className="text-neutral-600"> · by {p.created_by_name}</span>}</div>
                          {p.note && <div className="font-mont text-[10px] text-neutral-600 truncate">{p.note}</div>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="font-mont text-xs text-white">{fmtINR(p.amount)}</span>
                          <button title="Edit" onClick={() => openEditPay(p, r.e)} className="text-neutral-600 hover:text-white"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                          <button title="Delete" onClick={() => deletePay(p)} className="text-neutral-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-4 pt-3">
                  <button onClick={() => openPay(r, "advance")} className="font-mont text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> Advance</button>
                  <button onClick={() => openPay(r, "bonus")} className="font-mont text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> Bonus</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="font-mont text-[10px] text-neutral-600 mt-4 leading-relaxed">Salary records are kept separately and do not change your Finance totals or account balances. Advances count against the month they are recorded for; bonuses are extra.</p>

      {/* ---------- employee dialog ---------- */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 rounded-none text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{empEditing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className={labelCls}>Name</Label><Input data-testid="emp-name" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} className={fieldCls} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className={labelCls}>Designation</Label><Input value={empForm.designation} onChange={(e) => setEmpForm({ ...empForm, designation: e.target.value })} className={fieldCls} /></div>
              <div><Label className={labelCls}>Phone</Label><Input value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} className={fieldCls} /></div>
            </div>
            <div>
              <Label className={labelCls}>Monthly salary (₹)</Label>
              <Input data-testid="emp-salary" type="number" min="0" step="any" value={empForm.monthly_salary} onChange={(e) => setEmpForm({ ...empForm, monthly_salary: e.target.value })} className={fieldCls} />
            </div>
            {salaryChanged && (
              <div>
                <Label className={labelCls}>New salary applies from</Label>
                <div className="flex gap-3 mt-1">
                  <select value={(empForm.effective_from || month).slice(5, 7)} onChange={(e) => setEmpForm({ ...empForm, effective_from: `${(empForm.effective_from || month).slice(0, 4)}-${e.target.value}` })} className={selCls}>
                    {MONTHS.map((m, i) => (<option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>))}
                  </select>
                  <select value={(empForm.effective_from || month).slice(0, 4)} onChange={(e) => setEmpForm({ ...empForm, effective_from: `${e.target.value}-${(empForm.effective_from || month).slice(5, 7)}` })} className={selCls}>
                    {years.map((y) => (<option key={y} value={y}>{y}</option>))}
                  </select>
                </div>
                <p className="font-mont text-[10px] text-neutral-600 mt-1">Earlier months keep the old salary.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label className={labelCls}>Joined on</Label><Input type="date" value={empForm.joined} onChange={(e) => setEmpForm({ ...empForm, joined: e.target.value })} className={fieldCls + " [color-scheme:dark]"} /></div>
              <div><Label className={labelCls}>Left on (if any)</Label><Input type="date" value={empForm.left} onChange={(e) => setEmpForm({ ...empForm, left: e.target.value })} className={fieldCls + " [color-scheme:dark]"} /></div>
            </div>
            <div><Label className={labelCls}>Notes</Label><textarea rows={2} value={empForm.notes} onChange={(e) => setEmpForm({ ...empForm, notes: e.target.value })} className={taCls} /></div>
            <div className="flex items-center justify-between pt-2">
              {empEditing ? <button onClick={deleteEmp} className="font-mont text-[10px] uppercase tracking-[0.2em] text-neutral-600 hover:text-red-400">Delete employee</button> : <span />}
              <Button data-testid="save-employee-btn" onClick={saveEmp} disabled={saving} className="rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- payment dialog ---------- */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 rounded-none text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">{payEditing ? "Edit Payment" : "Record Payment"}{payForm ? ` — ${payForm.name}` : ""}</DialogTitle></DialogHeader>
          {payForm && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}>Type</Label>
                  <select data-testid="pay-kind" value={payForm.kind} onChange={(e) => setPayForm({ ...payForm, kind: e.target.value })} className={selCls}>
                    <option value="salary">Salary</option><option value="advance">Advance</option><option value="bonus">Bonus</option>
                  </select>
                </div>
                <div>
                  <Label className={labelCls}>For month</Label>
                  <Input type="month" value={payForm.month} onChange={(e) => setPayForm({ ...payForm, month: e.target.value })} className={fieldCls + " [color-scheme:dark]"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className={labelCls}>Amount (₹)</Label><Input data-testid="pay-amount" type="number" min="0" step="any" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={fieldCls} /></div>
                <div><Label className={labelCls}>Date paid</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className={fieldCls + " [color-scheme:dark]"} /></div>
              </div>
              <div>
                <Label className={labelCls}>Paid by</Label>
                <select value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })} className={selCls}>
                  {MODES.map((m) => (<option key={m} value={m}>{m || "—"}</option>))}
                </select>
              </div>
              <div><Label className={labelCls}>Note</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="Optional" className={fieldCls} /></div>
              <div className="flex justify-end pt-2">
                <Button data-testid="save-payment-btn" onClick={savePay} disabled={saving} className="rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
